import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import {
  selectContactConfig,
  selectPersonalContactSubscriptionData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { updateClientData } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { selectMcpUsers } from '@/store/PageConfiguration/PageConfigurationSelectors'

/**
 * Drives the MCP advisor picker on the Client tab.
 *
 * Visibility rules (must ALL be true):
 *   - country === 'fr'
 *   - logged-in user is a Selectra admin
 *   - the contact already has an `mcpAdvisoryId` assigned
 *
 * When the user picks a new advisor, we dispatch through `updateClientData`
 * with name='personal' — that:
 *   1. updates the slice state,
 *   2. flips the contact `isDirty` flag (no `_skipDirty` here),
 *   3. is picked up by the global Save button which appends
 *      `mcp_advisory_id` to the contact save payload.
 */
const useMcpAdvisor = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { country, isSelectraAdmin } = useSelector(selectAuthData)
  const { mcpAdvisoryId } = useSelector(selectPersonalContactSubscriptionData)
  const { contractLoaded } = useSelector(selectContactConfig)
  const mcpUsers = useSelector(selectMcpUsers)

  const isVisible =
    contractLoaded === true && country === 'fr' && isSelectraAdmin === true && Boolean(mcpAdvisoryId)

  // Shape expected by HOPSelect: { [id]: 'firstname name' }
  const optionsList = {}
  ;(mcpUsers ?? []).forEach(user => {
    if (user?.id !== undefined && user?.id !== null) {
      optionsList[String(user.id)] = `${user.firstname ?? ''} ${user.name ?? ''}`.trim()
    }
  })

  const updateAdvisor = newId => {
    if (newId === undefined || newId === null) return
    if (String(newId) === String(mcpAdvisoryId)) return
    dispatch(updateClientData({ data: { mcpAdvisoryId: newId }, name: 'personal' }))
  }

  return {
    t,
    isVisible,
    optionsList,
    selectedValue:
      mcpAdvisoryId !== null && mcpAdvisoryId !== undefined ? String(mcpAdvisoryId) : '',
    updateAdvisor,
  }
}

export default useMcpAdvisor
